'use client';

import { useEffect, useState } from 'react';

// HH:MM:SS for any duration. Trims to MM:SS under an hour so the
// surface (header pill, task row) stays narrow during the most common
// case. Pure helper — exported so callers can format snapshots without
// mounting the component.
export function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Returns ms elapsed since `startedAt`, ticking once a second. Cheap —
// one setInterval per mounted hook instance, only a handful active at
// once (header pill + one active task row).
export function useElapsedMs(startedAt: string): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now - new Date(startedAt).getTime();
}

// True once the elapsed window crosses the task's estimate. Estimate
// is in minutes (matches `tasks.timeEstimateMinutes`). `null` estimate
// means no expectation set, so never "over".
export function isOverEstimate(
  elapsedMs: number,
  estimateMinutes: number | null,
): boolean {
  if (estimateMinutes === null) return false;
  return elapsedMs >= estimateMinutes * 60_000;
}

interface LiveElapsedProps {
  /** ISO timestamp at which the elapsed counter starts. */
  startedAt: string;
  /** Optional className passed through to the rendered span. */
  className?: string;
}

// Convenience wrapper: live-ticking timecode in a span. Use this when
// you only need the rendered string. If you also need the raw elapsed
// (e.g. to decide overage colors), use `useElapsedMs` directly.
export function LiveElapsed({ startedAt, className }: LiveElapsedProps) {
  const elapsedMs = useElapsedMs(startedAt);
  return <span className={className}>{formatElapsed(elapsedMs)}</span>;
}
